/* eslint-disable react-hooks/exhaustive-deps */
import { Stack, FormLabel } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArtworkUploadImage from 'commons/ArtworkUploadImage';
import Text from 'commons/Text';
import { TESTID } from 'configs/Constant';
import useImageCompression from 'hooks/useImageCompression';
import useRole from 'hooks/useRole';
import useUploadImageS3 from 'hooks/useUploadImageS3';

import EditArtworkSection from '../../../../commons/BoxSection';
import useStyles from './styles';

const IMAGE_KEYS = {
  IMAGE1: 'image1',
  IMAGE2: 'image2',
  IMAGE3: 'image3',
  IMAGE4: 'image4',
  IMAGE5: 'image5',
};

const STATUS_IMAGE_KEYS = {
  IMAGE1: 'status_image1',
  IMAGE2: 'status_image2',
  IMAGE3: 'status_image3',
  IMAGE4: 'status_image4',
  IMAGE5: 'status_image5',
};

const EditArtworkImages = ({ artworkFormState, artwork }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isArtist } = useRole();

  const { generateImageKey } = useUploadImageS3();
  const { compressImage } = useImageCompression();

  const { setValue, watch, formState, register } = artworkFormState;

  const hasCertificate = formState?.defaultValues?.hasCertificate;
  const shouldDisableByCertificate = isArtist ? !artwork?.can_sync_certificates : hasCertificate;

  const listImage = watch('images') || [];
  const listKeyImage = watch('keyImages') || [];

  const rawStatusImages = watch('conditionImages') || [];
  const listKeyStatusImage = watch('keyConditionImages') || [];

  React.useEffect(() => {
    if (formState?.defaultValues?.condition_images?.length > 0 && !rawStatusImages.length) {
      setValue('conditionImages', formState.defaultValues.condition_images, {
        shouldValidate: true,
      });
    }
    if (formState?.defaultValues?.key_condition_images?.length > 0 && !listKeyStatusImage.length) {
      setValue('keyConditionImages', formState.defaultValues.key_condition_images);
    }
  }, [
    formState?.defaultValues?.condition_images,
    formState?.defaultValues?.key_condition_images,
    rawStatusImages.length,
    listKeyStatusImage.length,
    setValue,
  ]);

  const [displayImage, setDisplayImage] = useState({
    [IMAGE_KEYS.IMAGE1]: listImage[0],
    [IMAGE_KEYS.IMAGE2]: listImage[1],
    [IMAGE_KEYS.IMAGE3]: listImage[2],
    [IMAGE_KEYS.IMAGE4]: listImage[3],
    [IMAGE_KEYS.IMAGE5]: listImage[4],
  });

  const [keyImage, setKeyImage] = useState({
    [IMAGE_KEYS.IMAGE1]: listKeyImage[0],
    [IMAGE_KEYS.IMAGE2]: listKeyImage[1],
    [IMAGE_KEYS.IMAGE3]: listKeyImage[2],
    [IMAGE_KEYS.IMAGE4]: listKeyImage[3],
    [IMAGE_KEYS.IMAGE5]: listKeyImage[4],
  });

  const [uploadFile, setUploadFile] = useState({});

  // State for Current Status block
  const [displayStatusImage, setDisplayStatusImage] = useState({
    [STATUS_IMAGE_KEYS.IMAGE1]: rawStatusImages[0] || null,
    [STATUS_IMAGE_KEYS.IMAGE2]: rawStatusImages[1] || null,
    [STATUS_IMAGE_KEYS.IMAGE3]: rawStatusImages[2] || null,
    [STATUS_IMAGE_KEYS.IMAGE4]: rawStatusImages[3] || null,
    [STATUS_IMAGE_KEYS.IMAGE5]: rawStatusImages[4] || null,
  });

  const [keyStatusImage, setKeyStatusImage] = useState({
    [STATUS_IMAGE_KEYS.IMAGE1]: listKeyStatusImage[0] || null,
    [STATUS_IMAGE_KEYS.IMAGE2]: listKeyStatusImage[1] || null,
    [STATUS_IMAGE_KEYS.IMAGE3]: listKeyStatusImage[2] || null,
    [STATUS_IMAGE_KEYS.IMAGE4]: listKeyStatusImage[3] || null,
    [STATUS_IMAGE_KEYS.IMAGE5]: listKeyStatusImage[4] || null,
  });

  // Update display status images when rawStatusImages changes
  React.useEffect(() => {
    if (rawStatusImages?.length > 0) {
      setDisplayStatusImage({
        [STATUS_IMAGE_KEYS.IMAGE1]: rawStatusImages[0] || null,
        [STATUS_IMAGE_KEYS.IMAGE2]: rawStatusImages[1] || null,
        [STATUS_IMAGE_KEYS.IMAGE3]: rawStatusImages[2] || null,
        [STATUS_IMAGE_KEYS.IMAGE4]: rawStatusImages[3] || null,
        [STATUS_IMAGE_KEYS.IMAGE5]: rawStatusImages[4] || null,
      });
    }
  }, [rawStatusImages]);

  // Update key status images when listKeyStatusImage changes
  React.useEffect(() => {
    if (listKeyStatusImage?.length > 0) {
      setKeyStatusImage({
        [STATUS_IMAGE_KEYS.IMAGE1]: listKeyStatusImage[0] || null,
        [STATUS_IMAGE_KEYS.IMAGE2]: listKeyStatusImage[1] || null,
        [STATUS_IMAGE_KEYS.IMAGE3]: listKeyStatusImage[2] || null,
        [STATUS_IMAGE_KEYS.IMAGE4]: listKeyStatusImage[3] || null,
        [STATUS_IMAGE_KEYS.IMAGE5]: listKeyStatusImage[4] || null,
      });
    }
  }, [listKeyStatusImage]);

  const [uploadStatusFile, setUploadStatusFile] = useState({});

  const arrayFromObject = (object) => {
    return Object.values(object).filter((item) => !!item);
  };

  const handleUploadImage = (key) => async (e) => {
    // Only check hasCertificate for main images, not for status images
    if (shouldDisableByCertificate && !key.startsWith('status_')) {
      return;
    }

    const file = e?.target?.files[0];
    const compressedFile = await compressImage(file);
    const uri = URL.createObjectURL(compressedFile);
    const imageKey = generateImageKey(file);
    const data = {
      uri,
      uploadFile: compressedFile,
      imageKey,
    };

    const newDisplayImage = { ...displayImage, [key]: uri };
    const newKeyImage = { ...keyImage, [key]: imageKey };
    const newUploadFile = { ...uploadFile, [key]: data };

    setDisplayImage(newDisplayImage);
    setKeyImage(newKeyImage);
    setUploadFile(newUploadFile);

    // Update Form Value
    setValue('images', arrayFromObject(newDisplayImage), { shouldValidate: true });
    setValue('keyImages', arrayFromObject(newKeyImage));
    setValue('uploadFiles', arrayFromObject(newUploadFile));
  };

  const STATUS_KEYS_ORDER = [
    STATUS_IMAGE_KEYS.IMAGE1,
    STATUS_IMAGE_KEYS.IMAGE2,
    STATUS_IMAGE_KEYS.IMAGE3,
    STATUS_IMAGE_KEYS.IMAGE4,
    STATUS_IMAGE_KEYS.IMAGE5,
  ];

  const orderedValuesFromObject = (object, keysOrder = STATUS_KEYS_ORDER) => {
    return keysOrder.map((k) =>
      Object.prototype.hasOwnProperty.call(object, k) ? object[k] : null,
    );
  };

  const handleUploadStatusImage = (key) => async (e) => {
    const file = e?.target?.files[0];
    const compressedFile = await compressImage(file);
    const uri = URL.createObjectURL(compressedFile);
    const imageKey = generateImageKey(file);
    const data = {
      uri,
      uploadFile: compressedFile,
      imageKey,
    };

    const newDisplayImage = { ...displayStatusImage, [key]: uri };
    const newKeyImage = { ...keyStatusImage, [key]: imageKey };
    const newUploadFile = { ...uploadStatusFile, [key]: data };

    setDisplayStatusImage(newDisplayImage);
    setKeyStatusImage(newKeyImage);
    setUploadStatusFile(newUploadFile);

    const statusImagesArray = orderedValuesFromObject(newDisplayImage);
    const statusKeyImagesArray = orderedValuesFromObject(newKeyImage);
    const statusUploadFilesArray = orderedValuesFromObject(newUploadFile);

    setValue('conditionImages', statusImagesArray, { shouldValidate: true });
    setValue('keyConditionImages', statusKeyImagesArray);
    setValue('statusUploadFiles', statusUploadFilesArray);
  };
  9;

  const handleRemoveStatusImage = (key) => () => {
    const newDisplayImage = { ...displayStatusImage, [key]: null };
    const newKeyImage = { ...keyStatusImage, [key]: null };
    const newUploadFile = { ...uploadStatusFile, [key]: null };

    setDisplayStatusImage(newDisplayImage);
    setKeyStatusImage(newKeyImage);
    setUploadStatusFile(newUploadFile);

    // Giữ vị trí bằng cách trả về mảng có null tại vị trí bị xóa
    const statusImagesArray = orderedValuesFromObject(newDisplayImage);
    const statusKeyImagesArray = orderedValuesFromObject(newKeyImage);
    const statusUploadFilesArray = orderedValuesFromObject(newUploadFile);

    setValue('conditionImages', statusImagesArray, { shouldValidate: true });
    setValue('keyConditionImages', statusKeyImagesArray);
    setValue('statusUploadFiles', statusUploadFilesArray);
  };

  const handleRemoveImage = (key) => () => {
    if (shouldDisableByCertificate && !key.startsWith('status_')) {
      return;
    }

    const newDisplayImage = { ...displayImage, [key]: null };
    const newKeyImage = { ...keyImage, [key]: null };
    const newUploadFile = { ...uploadFile, [key]: null };

    setDisplayImage(newDisplayImage);
    setKeyImage(newKeyImage);
    setUploadFile(newUploadFile);

    setValue('images', arrayFromObject(newDisplayImage), { shouldValidate: true });
    setValue('keyImages', arrayFromObject(newKeyImage));
    setValue('uploadFiles', arrayFromObject(newUploadFile));
  };

  return (
    <EditArtworkSection
      sx={{ mt: 2 }}
      heading={t('artwork_images')}
    >
      <Stack
        sx={{ mt: 3 }}
        direction={{ lg: 'row', xs: 'column' }}
        spacing={2}
      >
        <ArtworkUploadImage
          image={displayImage[IMAGE_KEYS.IMAGE1]}
          onUploadImage={handleUploadImage(IMAGE_KEYS.IMAGE1)}
          disabled={shouldDisableByCertificate}
          onRemoveImage={handleRemoveImage(IMAGE_KEYS.IMAGE1)}
          isRequired
          alt="image front"
          removeIconTestId={`${TESTID.ICON_REMOVE_IMAGE}_IMAGE_FRONT`}
          textRecommend={t('front')}
          wrapStyle={shouldDisableByCertificate && classes.notAllow}
        />
        <ArtworkUploadImage
          image={displayImage[IMAGE_KEYS.IMAGE2]}
          onUploadImage={handleUploadImage(IMAGE_KEYS.IMAGE2)}
          disabled={shouldDisableByCertificate}
          onRemoveImage={handleRemoveImage(IMAGE_KEYS.IMAGE2)}
          alt="image back"
          textRecommend={t('back_side')}
          wrapStyle={shouldDisableByCertificate && classes.notAllow}
        />
        <ArtworkUploadImage
          image={displayImage[IMAGE_KEYS.IMAGE3]}
          onUploadImage={handleUploadImage(IMAGE_KEYS.IMAGE3)}
          disabled={shouldDisableByCertificate}
          onRemoveImage={handleRemoveImage(IMAGE_KEYS.IMAGE3)}
          alt="image detail"
          textRecommend={t('detail')}
          uploadImageTestId={`${TESTID.INPUT_UPLOAD_IMAGE}_IMAGE_DETAIL`}
          wrapStyle={shouldDisableByCertificate && classes.notAllow}
        />
        <ArtworkUploadImage
          image={displayImage[IMAGE_KEYS.IMAGE4]}
          onUploadImage={handleUploadImage(IMAGE_KEYS.IMAGE4)}
          disabled={shouldDisableByCertificate}
          onRemoveImage={handleRemoveImage(IMAGE_KEYS.IMAGE4)}
          alt="image signature"
          textRecommend={t('signature')}
          wrapStyle={shouldDisableByCertificate && classes.notAllow}
        />
        <ArtworkUploadImage
          image={displayImage[IMAGE_KEYS.IMAGE5]}
          onUploadImage={handleUploadImage(IMAGE_KEYS.IMAGE5)}
          disabled={shouldDisableByCertificate}
          onRemoveImage={handleRemoveImage(IMAGE_KEYS.IMAGE5)}
          alt="image damage"
          textRecommend={t('damage')}
          wrapStyle={shouldDisableByCertificate && classes.notAllow}
        />
      </Stack>

      {shouldDisableByCertificate && !isArtist && (
        <div>
          <Text
            type="xl"
            fontWeightBold
            mt={24}
          >
            {t('historical_images')}
          </Text>
          <Text type="md">{t('you_can_only_update_the_current_status')}</Text>
          <Stack
            sx={{ mt: 3 }}
            direction={{ lg: 'row', xs: 'column' }}
            spacing={2}
          >
            <ArtworkUploadImage
              image={displayStatusImage[STATUS_IMAGE_KEYS.IMAGE1]}
              onUploadImage={handleUploadStatusImage(STATUS_IMAGE_KEYS.IMAGE1)}
              onRemoveImage={handleRemoveStatusImage(STATUS_IMAGE_KEYS.IMAGE1)}
              isRequired
              alt="image front"
              removeIconTestId={`${TESTID.ICON_REMOVE_IMAGE}_IMAGE_FRONT`}
              textRecommend={t('front')}
            />
            <ArtworkUploadImage
              image={displayStatusImage[STATUS_IMAGE_KEYS.IMAGE2]}
              onUploadImage={handleUploadStatusImage(STATUS_IMAGE_KEYS.IMAGE2)}
              onRemoveImage={handleRemoveStatusImage(STATUS_IMAGE_KEYS.IMAGE2)}
              alt="image back"
              textRecommend={t('back_side')}
            />
            <ArtworkUploadImage
              image={displayStatusImage[STATUS_IMAGE_KEYS.IMAGE3]}
              onUploadImage={handleUploadStatusImage(STATUS_IMAGE_KEYS.IMAGE3)}
              onRemoveImage={handleRemoveStatusImage(STATUS_IMAGE_KEYS.IMAGE3)}
              alt="image detail"
              textRecommend={t('detail')}
              uploadImageTestId={`${TESTID.INPUT_UPLOAD_IMAGE}_IMAGE_DETAIL`}
            />
            <ArtworkUploadImage
              image={displayStatusImage[STATUS_IMAGE_KEYS.IMAGE4]}
              onUploadImage={handleUploadStatusImage(STATUS_IMAGE_KEYS.IMAGE4)}
              onRemoveImage={handleRemoveStatusImage(STATUS_IMAGE_KEYS.IMAGE4)}
              alt="image signature"
              textRecommend={t('signature')}
            />
            <ArtworkUploadImage
              image={displayStatusImage[STATUS_IMAGE_KEYS.IMAGE5]}
              onUploadImage={handleUploadStatusImage(STATUS_IMAGE_KEYS.IMAGE5)}
              onRemoveImage={handleRemoveStatusImage(STATUS_IMAGE_KEYS.IMAGE5)}
              alt="image damage"
              textRecommend={t('damage')}
            />
          </Stack>
        </div>
      )}
      <FormLabel
        error={!!formState?.errors.images}
        sx={{ mt: 1, display: 'block' }}
        ref={register('images').ref}
        tabIndex={0}
      >
        {formState?.errors.images?.message}
      </FormLabel>
    </EditArtworkSection>
  );
};

export default EditArtworkImages;
